/**
 * Operator-facing text for the print-file partial-progress wire codes.
 *
 * BLU-663 review: `resolveAfterUpload` used to return an English `detail`
 * sentence that the UI rendered verbatim — untranslatable, and a zh operator
 * got English inside an otherwise-Chinese dialog. The router now returns a
 * stable `detailCode` (`PRINT_FILE_DETAIL_CODES` in @threadheads/shared) and
 * the English prose lives only in the backend log line. This is the single
 * client-side mapping; the dialog and the bulk modal both go through it so
 * they can never word the same server state differently.
 */
import type { TFunction } from 'i18next';
import { PRINT_FILE_DETAIL_CODES, type PrintFileDetailCode } from '@threadheads/shared';

const DETAIL_CODES: ReadonlySet<string> = new Set(PRINT_FILE_DETAIL_CODES);

function readDetailCode(result: unknown): PrintFileDetailCode | null {
  if (!result || typeof result !== 'object') return null;
  const code = (result as { detailCode?: unknown }).detailCode;
  return typeof code === 'string' && DETAIL_CODES.has(code) ? (code as PrintFileDetailCode) : null;
}

/**
 * Translate a mutation result's `detailCode`, or null when it carries none
 * (a resolved response, or an older server). Callers fall back to their own
 * generic copy — never to an untranslated string.
 */
export function printFileDetailMessage(
  t: TFunction,
  result: unknown,
): string | null {
  const code = readDetailCode(result);
  return code ? t(`issues.printFiles.upload.detail.${code}`) : null;
}
